import React from 'react'
import { useIntl } from 'react-intl'

type BadgeVariant = 'primary' | 'secondary' | 'success' | 'warning' | 'error' | 'info' | 'neutral'

interface BadgeProps {
  children: React.ReactNode
  variant?: BadgeVariant
  size?: 'sm' | 'md'
  className?: string
}

const variantStyles: Record<BadgeVariant, string> = {
  primary: 'bg-primary/10 text-primary-dark',
  secondary: 'bg-secondary/10 text-secondary-dark',
  success: 'bg-success-light text-success',
  warning: 'bg-warning-light text-warning',
  error: 'bg-error-light text-error',
  info: 'bg-info-light text-info',
  neutral: 'bg-background-alt text-foreground-muted',
}

const sizeStyles = {
  sm: 'px-2 py-0.5 text-xs',
  md: 'px-2.5 py-0.5 text-sm',
}

export const Badge: React.FC<BadgeProps> = ({
  children,
  variant = 'neutral',
  size = 'sm',
  className = '',
}) => {
  return (
    <span className={`
      inline-flex items-center font-medium rounded-full
      ${variantStyles[variant]}
      ${sizeStyles[size]}
      ${className}
    `}>
      {children}
    </span>
  )
}

// Backend status/role enums that have a localized label. Others render raw.
const KNOWN_STATUSES = new Set([
  'PUBLISHED', 'DRAFT', 'CLOSED', 'PENDING', 'ACTIVE', 'COMPLETED', 'OVERDUE',
  'WAIVED', 'OWNER', 'ADMIN', 'RESPONDENT', 'ACCEPTED', 'EXPIRED', 'CANCELLED',
])

export const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const intl = useIntl()
  const variantMap: Record<string, BadgeVariant> = {
    PUBLISHED: 'success',
    DRAFT: 'warning',
    CLOSED: 'neutral',
    PENDING: 'warning',
    ACTIVE: 'success',
    COMPLETED: 'success',
    OVERDUE: 'error',
    WAIVED: 'neutral',
    OWNER: 'primary',
    ADMIN: 'info',
    RESPONDENT: 'neutral',
  }
  const label = KNOWN_STATUSES.has(status)
    ? intl.formatMessage({ id: `status.${status}` })
    : status
  return <Badge variant={variantMap[status] || 'neutral'}>{label}</Badge>
}
